import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Badge,
  Select,
  Stack,
  Flex
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { updateBusinessStatusAction, fetchBusinessesAction } from 'state/actions/businessActions';
import { fetchStatusHistoryAction } from 'state/actions/statusHistoryActions.js';
import { useCustomNotification } from 'components/Toasts/OpenNotification';
import ReusableDrawer from '../../../../components/Drawers/ReusableDrawer.js';
import GradientButton from 'components/Buttons/GradientButton.js';
import ReusableInput from 'components/Inputs/ReusableInput';
import StatusLogs from 'components/Card/StatusLogs.js';
import headerImage from '../../../../assets/img/background.png';

const EditBusinessDrawer = ({ business, isOpen, onClose }) => {
  const notify = useCustomNotification();
  const dispatch = useDispatch();

  const [status, setStatus] = useState(business?.status || '');
  const [reason, setReason] = useState('');

  const { history, loading } = useSelector((state) => state.statusHistory);

  useEffect(() => {
    setStatus(business?.status || '');
    setReason('');
  }, [business]);

  useEffect(() => {
    if (business?.business_id) {
      dispatch(
        fetchStatusHistoryAction("business", business?.business_id)
      );
    }
  }, [dispatch, business]);

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleSave = async () => {
    try {
      const response = await dispatch(
        updateBusinessStatusAction(
          business?.business_id,
          status,
          reason
        )
      );

      if (response.success) {
        notify({
          message: 'Business Status Updated',
          description: `Status changed to "${status}"`,
          type: 'success',
        });
        onClose();
        dispatch(fetchBusinessesAction());
      } else {
        notify({
          message: 'Failed to Update Status',
          description: response.error || 'An error occurred while updating status.',
          type: 'error',
        });
      }
    } catch (error) {
      notify({
        message: 'Error',
        description: error.message || 'Something went wrong while updating the status.',
        type: 'error',
      });
    }
  };

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={onClose}
      headerImage={headerImage}
      headerContent={<h2>Edit Business Status</h2>}
      bodyContent={
        <Box>
          {/* Current Status */}
          <Text fontWeight="500" mb={1} fontSize="sm" color="#8D93A1">
            Current Status
          </Text>
          <Badge
            colorScheme={
              status === 'Approved' ? 'green' :
              status === 'Suspended' ? 'red' :
              status === 'Pending' ? 'yellow' :
              status === 'Rejected' ? 'orange' :
              status === 'Deactivated' ? 'purple' :
              'gray'
            }
            mb={4}
            borderRadius="md"
          >
            {status}
          </Badge>

          {/* Update Status */}
          <Text fontWeight="500" mb={1} fontSize="sm" color="#8D93A1">
            Update Status
          </Text>
          <Select
            placeholder="Select status"
            value={status}
            onChange={handleStatusChange}
            mb={4}
          >
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Suspended">Suspended</option>
            <option value="Deactivated">Deactivated</option>
            <option value="Rejected">Rejected</option>
          </Select>

          {/* Reason */}
          <ReusableInput
            label="Reason for Status Change"
            placeholder="Provide a brief reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            type="textarea"
          />

          {/* Status History */}
          <Box mt={6}>
            <Text fontWeight="600" mb={3} fontSize="sm" color="#8D93A1">
              Status History
            </Text>

            {loading ? (
              <Flex overflowX="auto" gap={4}>
                {[1, 2, 3].map((item) => (
                  <Box
                    key={item}
                    minW="260px"
                    p={4}
                    borderRadius="md"
                    bg="gray.50"
                  >
                    <Box h="20px" w="80px" bg="gray.200" borderRadius="md" mb={3} />

                    <Box h="10px" w="70%" bg="gray.200" mb={2} />
                    <Box h="10px" w="90%" bg="gray.200" mb={2} />
                    <Box h="10px" w="60%" bg="gray.200" mb={2} />

                    <Box h="10px" w="50%" bg="gray.200" mb={3} />

                    <Box h="10px" w="100%" bg="gray.200" mb={2} />
                    <Box h="10px" w="80%" bg="gray.200" mb={2} />

                    <Flex justify="flex-end" mt={3}>
                      <Box h="30px" w="30px" bg="gray.200" borderRadius="full" />
                    </Flex>
                  </Box>
                ))}
              </Flex>
            ) : (
              <StatusLogs logs={history} />
            )}
          </Box>
        </Box>
      }
      footerContent={
        <Stack direction="row" spacing={4} justifyContent="flex-end">
          <GradientButton onClick={onClose} fontSize="15px" width="100px" height="40px">
            Cancel
          </GradientButton>
          <GradientButton onClick={handleSave} fontSize="15px" width="100px" height="40px">
            Save
          </GradientButton>
        </Stack>
      }
    />
  );
};

export default EditBusinessDrawer;

